import { useState } from 'react';
import { Alert, Pressable, Text, View } from 'react-native';
import { copiarParaClientes } from '../lib/clientes';
import type { Reg } from '../store/types';
import type { Colecao, Ctx, ModDef, PainelProps } from './tipos';
import { contagem, dataBr, din, dinDe, dinSomas, junta, num, op, pct, somaPorMoeda, temNum, temSoma } from './util';
import { mudarCampos, mudarReg, novoReg, salvarReg, salvarVarios } from '../store/modulos';
import { whatsapp, abrirSite } from '../lib/mapa';
import { Btn, Chip, Field } from '../components/ui';
import { C, alpha } from '../components/theme';
import { SEMANA_CURTA, addDays, longDate, parseYmd, today, ymd, horaTela } from '../lib/date';
import { N_, t } from '../lib/i18n';
import { useFeriados } from '../components/useFeriados';
import { PickerModal } from '../components/Pickers';
import { cobrarAlunos, cobrarAulaExtra, diasDaTurma, ehDiaDeAula, enviarMaterial, enviarNotas, nomesDosDias, outroDiaDeAula, proximoDiaDeAula } from './professorTurma';
import { acaoRelatorioPdf, dadosPdf, porDataPdf, secaoPdf } from '../lib/pdfFicha';
import type { BlocoPdf } from '../lib/pdf';
import { colecoesOndeTrabalho } from './ondeTrabalho';
import { HORA_AULA, SALARIO, ondeTrabalhoLigado } from './ondeTrabalhoBase';

const alunosDe = (ctx: Ctx, turma: string) => ctx.filhos(turma, 'aluno').sort((a, b) => (num(a.c.numero) - num(b.c.numero)) || String(a.c.nome).localeCompare(String(b.c.nome)));
/** As notas dos alunos numa avaliação (c.notas é sempre o objeto aluno → nota; o texto de "Anotações" fica em c.anotacoes). */
const notasObj = (c: Record<string, any>): Record<string, any> => (c.notas && typeof c.notas === 'object' && !Array.isArray(c.notas) ? c.notas : {});
const fmtNota = (n: number) => (Math.round(n * 10) / 10).toString().replace('.', ',');

/** Frequência e média de um aluno na turma. "Outros" (dispensado, atestado...) não conta como aula nem como falta. */
export function situacaoAluno(ctx: Ctx, a: Reg) {
  const chamadas = ctx.filhos(a.pai!, 'chamada').filter(c => !(c.c.outros ?? []).includes(a.id));
  const faltas = chamadas.filter(c => (c.c.faltas ?? []).includes(a.id)).length;
  const avals = ctx.filhos(a.pai!, 'avaliacao').filter(v => v.c.notas?.[a.id] != null && v.c.notas[a.id] !== '');
  const peso = avals.reduce((s, v) => s + (num(v.c.peso) || 1), 0);
  const media = peso ? avals.reduce((s, v) => s + (num(v.c.notas[a.id]) / (num(v.c.max) || 10)) * 10 * (num(v.c.peso) || 1), 0) / peso : null;
  return { faltas, aulas: chamadas.length, freq: chamadas.length ? 100 - pct(faltas, chamadas.length) : null, media };
}

/** Chamada do dia: toque no aluno para trocar entre presente, falta e outros (0.65).
 *  Mostra só os dias de aula da turma; outro dia vira reposição ou aula extra. Avisa quando o dia é feriado. */
function Chamada({ ctx, pai, pode }: PainelProps) {
  const dias = diasDaTurma(pai);
  const [dia, setDia] = useState(() => {
    const h = today();
    if (!pai || ehDiaDeAula(dias, h) || ctx.filhos(pai.id, 'chamada').some(c => c.c.data === h)) return h;
    return outroDiaDeAula(dias, h, -1);
  });
  const [livre, setLivre] = useState(false);
  const [marca, setMarca] = useState<{ reposicao?: boolean; extra?: boolean }>({});
  const { doDia } = useFeriados();
  if (!pai) return null;
  const alunos = alunosDe(ctx, pai.id);
  const doDiaRegs = ctx.filhos(pai.id, 'chamada');
  const reg = doDiaRegs.find(c => c.c.data === dia);
  const faltas: string[] = reg?.c.faltas ?? [];
  const outros: string[] = reg?.c.outros ?? [];
  const deAula = ehDiaDeAula(dias, dia);
  const feriados = doDia(dia);
  const flags = reg ? { reposicao: !!reg.c.reposicao, extra: !!reg.c.extra } : { reposicao: !!marca.reposicao || (!deAula && !marca.extra), extra: !!marca.extra };

  function ir(passo: 1 | -1) {
    setMarca({});
    if (livre || !dias.length) return setDia(addDays(dia, passo));
    // pula para o próximo dia de aula, parando também nos dias com chamada já feita (reposição, aula extra)
    let d = addDays(dia, passo);
    for (let i = 0; i < 7 && !ehDiaDeAula(dias, d) && !doDiaRegs.some(c => c.c.data === d); i++) d = addDays(d, passo);
    setDia(d);
  }
  /** Antes da primeira marcação do dia: confirma se for feriado. */
  function criar(c: Record<string, any>) {
    const fazer = () => salvarReg(ctx.mod, novoReg('chamada', pai!.id, { data: dia, faltas: [], outros: [], ...flags, ...c }));
    if (!feriados.length) return fazer();
    const nomes = feriados.map(f => f.nome).join(', ');
    Alert.alert(t('Feriado'), dia === today() ? t('Hoje é feriado: {f}. Registrar mesmo assim?', { f: nomes }) : t('{d} é feriado: {f}. Registrar mesmo assim?', { d: dataBr(dia), f: nomes }), [
      { text: t('Cancelar'), style: 'cancel' }, { text: t('Registrar'), onPress: fazer },
    ]);
  }
  function marcar(id: string) {
    if (pode === false) return Alert.alert(t('Só leitura'), t('O seu papel no grupo não permite mudar aqui.'));   // 0.77
    // presente → falta → outros → presente
    const f = faltas.includes(id), o = outros.includes(id);
    const nf = f ? faltas.filter(x => x !== id) : o ? faltas : [...faltas, id];
    const no = f ? [...outros, id] : outros.filter(x => x !== id);
    if (reg) mudarCampos(ctx.mod, reg.id, { faltas: nf, outros: no });
    else criar({ faltas: nf, outros: no });
  }
  function alternar(k: 'reposicao' | 'extra') {
    if (pode === false) return;
    if (reg) mudarCampos(ctx.mod, reg.id, { [k]: !reg.c[k] });
    else setMarca(m => ({ ...flags, ...m, [k]: !flags[k] }));
  }
  const presentes = alunos.filter(a => !faltas.includes(a.id) && !outros.includes(a.id)).length;
  const nOutros = outros.filter(o => alunos.some(a => a.id === o)).length;
  return (
    <View style={{ gap: 8 }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
        <Pressable onPress={() => ir(-1)} hitSlop={10}><Text style={{ color: C.sub, fontSize: 22 }}>‹</Text></Pressable>
        <Text style={{ color: C.text, fontWeight: '800', flex: 1, textAlign: 'center' }}>{longDate(dia)}</Text>
        <Pressable onPress={() => ir(1)} hitSlop={10}><Text style={{ color: C.sub, fontSize: 22 }}>›</Text></Pressable>
      </View>
      <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        {!!dias.length && <Chip small cor={ctx.cor} on={livre} label={'📅 ' + t('Todos os dias')} onPress={() => setLivre(x => !x)} />}
        {dia !== today() && <Chip small cor={ctx.cor} on={false} label={t('Hoje')} onPress={() => { setMarca({}); setDia(today()); }} />}
        <Chip small cor={ctx.cor} on={flags.reposicao} label={'🔁 ' + t('Reposição')} onPress={() => alternar('reposicao')} />
        <Chip small cor={ctx.cor} on={flags.extra} label={'➕ ' + t('Aula extra')} onPress={() => alternar('extra')} />
      </View>
      {dias.length ? <Text style={{ color: C.mute, fontSize: 12 }}>{t('Dias de aula: {d}', { d: nomesDosDias(dias) })}</Text>
        : <Text style={{ color: C.mute, fontSize: 12 }}>{t('Marque os dias de aula na turma (Editar) para a chamada mostrar só esses dias.')}</Text>}
      {!!feriados.length && <Text style={{ color: C.warn, fontSize: 13, fontWeight: '700' }}>🎉 {t('Feriado: {f}', { f: feriados.map(f => f.nome).join(', ') })}</Text>}
      {!deAula && !reg && <Text style={{ color: C.warn, fontSize: 13 }}>{t('Não é dia de aula desta turma. A chamada fica como reposição ou aula extra.')}</Text>}
      {!alunos.length && <Text style={{ color: C.sub }}>{t('Cadastre os alunos da turma na aba Alunos.')}</Text>}
      {!!alunos.length && (
        <Text style={{ color: C.sub, fontSize: 12 }}>
          {reg ? junta(t('{p} presentes · {f} faltas', { p: presentes, f: faltas.filter(f => alunos.some(a => a.id === f)).length }), nOutros > 0 && t('{n} outro(s)', { n: nOutros }))
            : t('Chamada ainda não feita neste dia. Toque em quem faltou ou confirme todos presentes.')}
        </Text>
      )}
      {!!alunos.length && <Text style={{ color: C.mute, fontSize: 11 }}>{t('Toque de novo para "Outros" (dispensado, atestado...): não conta como falta nem como aula.')}</Text>}
      {alunos.map(a => {
        const falta = faltas.includes(a.id), outro = outros.includes(a.id);
        return (
          <Pressable key={a.id} onPress={() => marcar(a.id)} style={{ flexDirection: 'row', alignItems: 'center', gap: 10, backgroundColor: falta ? alpha(C.danger, 0.15) : outro ? alpha(C.warn, 0.15) : C.card, borderRadius: 12, padding: 12 }}>
            <Text style={{ color: C.mute, width: 26 }}>{num(a.c.numero) || ''}</Text>
            <Text style={{ color: C.text, flex: 1 }}>{a.c.nome}</Text>
            <Text style={{ color: falta ? C.danger : outro ? C.warn : reg ? C.ok : C.mute, fontWeight: '800' }}>{falta ? t('Falta') : outro ? t('Outros') : reg ? t('Presente') : '—'}</Text>
          </Pressable>
        );
      })}
      {!!alunos.length && !reg && <Pressable onPress={() => criar({})} style={{ backgroundColor: ctx.cor, borderRadius: 12, padding: 12, alignItems: 'center' }}><Text style={{ color: '#fff', fontWeight: '800' }}>✓ {t('Todos presentes')}</Text></Pressable>}
      {!!reg?.c.extra && <Pressable onPress={() => cobrarAulaExtra(ctx, pai.id, dia)} style={{ borderWidth: 1, borderColor: ctx.cor, borderRadius: 12, padding: 12, alignItems: 'center' }}><Text style={{ color: ctx.cor, fontWeight: '800' }}>💰 {t('Cobrar a aula extra dos presentes')}</Text></Pressable>}
    </View>
  );
}

function NotaInput({ valor, onSalvar, pode }: { valor: any; onSalvar: (v: number | '') => void; pode?: boolean }) {
  const [txt, setTxt] = useState(valor === undefined || valor === '' ? '' : fmtNota(num(valor)));
  if (pode === false) return <Text style={{ color: C.text, width: 80, textAlign: 'center' }}>{txt || '—'}</Text>;   // só leitura (0.77)
  const salvar = () => { const s = txt.trim(); onSalvar(s ? num(s) : ''); };
  return <Field value={txt} onChangeText={setTxt} onBlur={salvar} onSubmitEditing={salvar} keyboardType="decimal-pad" placeholder="—" style={{ width: 80, textAlign: 'center', paddingVertical: 8 }} />;
}

/** Avaliações com a nota de cada aluno. */
function Notas({ ctx, pai, lista, abrir, novo, pode }: PainelProps) {
  const [aberta, setAberta] = useState<string | null>(null);
  if (!pai) return null;
  const alunos = alunosDe(ctx, pai.id);
  const ordenadas = [...lista].sort((a, b) => String(a.c.data || '9').localeCompare(String(b.c.data || '9')));
  return (
    <View style={{ gap: 8 }}>
      {!ordenadas.length && <Pressable onPress={() => novo()}><Text style={{ color: ctx.cor, fontWeight: '700' }}>+ {t('Nova avaliação')}</Text></Pressable>}
      {ordenadas.map(v => {
        const notas: Record<string, any> = notasObj(v.c);
        const dadas = alunos.filter(a => notas[a.id] !== undefined && notas[a.id] !== '');
        const media = dadas.length ? dadas.reduce((s, a) => s + num(notas[a.id]), 0) / dadas.length : null;
        const cont = contagem(v.c.data);
        return (
          <View key={v.id} style={{ backgroundColor: C.card, borderRadius: 14, padding: 12, gap: 6 }}>
            <Pressable onPress={() => setAberta(aberta === v.id ? null : v.id)} onLongPress={() => abrir(v)} delayLongPress={1000} style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <Text style={{ color: C.mute }}>{aberta === v.id ? '▾' : '▸'}</Text>
              <Text style={{ fontSize: 20 }}>📝</Text>
              <View style={{ flex: 1 }}>
                <Text style={{ color: C.text, fontWeight: '700' }}>{v.c.nome || t('Avaliação')}</Text>
                <Text style={{ color: C.sub, fontSize: 12 }}>{junta(v.c.data && `${dataBr(v.c.data)} (${cont?.txt})`, t('vale {n}', { n: num(v.c.max) || 10 }), num(v.c.peso) > 1 && t('peso {n}', { n: num(v.c.peso) }),
                  t('{d}/{n} notas', { d: dadas.length, n: alunos.length }), media != null && t('média {m}', { m: fmtNota(media) }))}</Text>
              </View>
              <Pressable onPress={() => abrir(v)} hitSlop={8}><Text style={{ color: C.sub }}>✏️</Text></Pressable>
            </Pressable>
            {aberta === v.id && alunos.map(a => (
              <View key={a.id} style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                <Text style={{ color: C.text, flex: 1 }} numberOfLines={1}>{a.c.nome}</Text>
                <NotaInput pode={pode} valor={notas[a.id]} onSalvar={x => mudarReg(ctx.mod, v.id, y => ({ ...y, c: { ...y.c, notas: { ...notasObj(y.c), [a.id]: x } } }))} />
              </View>
            ))}
            {aberta === v.id && pode !== false && !!dadas.length && (
              <Pressable onPress={() => enviarNotas(ctx, v, fmtNota)} style={{ paddingVertical: 6 }}>
                <Text style={{ color: ctx.cor, fontWeight: '700' }}>📤 {t('Enviar a nota para cada aluno')}</Text>
              </Pressable>
            )}
          </View>
        );
      })}
    </View>
  );
}

const turma: Colecao = {
  id: 'turma', nome: N_('Turmas'), um: N_('Turma'), icone: '🏫',
  campos: [
    { k: 'nome', r: N_('Turma'), t: 'texto', ph: N_('Ex.: 8º ano B') },
    { k: 'disciplina', r: N_('Disciplina'), t: 'texto' },
    { k: 'escola', r: N_('Escola'), t: 'texto' },
    { k: 'endEscola', r: N_('Endereço da escola'), t: 'endereco' },
    { k: 'horario', r: N_('Dias e horários'), t: 'texto', ph: N_('Ex.: seg e qua, 07:30') },
    { k: 'dias', r: N_('Dias de aula'), t: 'multi', op: SEMANA_CURTA },
    { k: 'sala', r: N_('Sala'), t: 'texto' },
    { k: 'media', r: N_('Média para aprovação'), t: 'numero' },
    { k: 'mensalidade', r: N_('Mensalidade ou valor por aluno'), t: 'dinheiro' },
    { k: 'diaVenc', r: N_('Dia do vencimento'), t: 'numero' },
    { k: 'valorExtra', r: N_('Valor da aula extra (por aluno)'), t: 'dinheiro' },
    { k: 'link', r: N_('Link da sala online (Meet, Zoom...)'), t: 'link' },
    // D4 (Onde trabalho): a turma de uma instituição que paga por hora-aula entra na conta das aulas dadas
    { k: 'instituicao', r: N_('Instituição (Onde trabalho)'), t: 'ref', ref: 'instituicao', mostrar: (_c, ctx) => !!ctx?.regs.some(r => r.col === 'instituicao') && ondeTrabalhoLigado('professor') },
    { k: 'hAula', r: N_('Horas-aula de cada aula'), t: 'numero', mostrar: c => !!c.instituicao },
  ],
  titulo: r => junta(r.c.nome, r.c.disciplina), sub: (r, ctx) => junta(r.c.escola, r.c.horario, t('{n} aluno(s)', { n: ctx.filhos(r.id, 'aluno').length })),
  ordem: 'titulo', novo: () => ({ media: 6 }),
  acoes: (r, ctx) => [
    ...(r.c.link ? [{ label: '🎥 ' + t('Entrar na sala online'), onPress: () => abrirSite(r.c.link) }] : []),
    { label: '💰 ' + t('Gerar as cobranças do mês'), onPress: () => gerarCobrancas(ctx, r) },
    acaoRelatorioPdf(() => relatorioTurma(ctx, r)),   // 0.65 (B15)
  ],
  vazio: N_('Crie a turma (ou grupo de mentoria, treino, curso) e, dentro dela, os alunos, a chamada, as notas, os planos de aula, os materiais e as cobranças.'),
};

const aluno: Colecao = {
  id: 'aluno', pai: 'turma', nome: N_('Alunos'), um: N_('Aluno'), icone: '🧑‍🎓',
  campos: [
    { k: 'nome', r: N_('Nome'), t: 'texto' },
    { k: 'numero', r: N_('Número na chamada'), t: 'numero' },
    { k: 'responsavel', r: N_('Responsável'), t: 'texto' },
    { k: 'telefone', r: N_('Telefone'), t: 'telefone' },
    { k: 'email', r: N_('E-mail do responsável'), t: 'email' },
    { k: 'valor', r: N_('Valor que este aluno paga (se for diferente)'), t: 'dinheiro' },
    { k: 'notas', r: N_('Observações'), t: 'longo' },
  ],
  // texto 25 (A3): só nome e contato (nada da Área do aluno)
  acoes: r => [{ label: '👥 ' + t('Copiar para Meus clientes'), onPress: () => copiarParaClientes({ nome: r.c.nome, telefone: r.c.telefone, email: r.c.email }) }],
  titulo: r => r.c.nome || '',
  sub: (r, ctx) => { const s = situacaoAluno(ctx, r); const minimo = num(ctx.porId(r.pai)?.c.media) || 6;
    return junta(s.freq != null && t('frequência {p}%', { p: s.freq }), s.faltas && t('{n} falta(s)', { n: s.faltas }), s.media != null && `${t('média {m}', { m: fmtNota(s.media) })}${s.media < minimo ? ' ⚠️' : ''}`); },
  ordem: (a, b) => (num(a.c.numero) - num(b.c.numero)) || String(a.c.nome).localeCompare(String(b.c.nome)),
  novo: (ctx, pai) => ({ numero: ctx.regs.filter(r => r.col === 'aluno' && r.pai === pai?.id).length + 1 }),
};

const chamada: Colecao = {
  id: 'chamada', pai: 'turma', nome: N_('Chamada'), um: N_('Chamada'), icone: '🙋',
  campos: [{ k: 'data', r: N_('Data'), t: 'data' }],
  titulo: r => junta(dataBr(r.c.data), r.c.reposicao && t('reposição'), r.c.extra && t('aula extra')), lista: Chamada, semNovo: true,
};

const avaliacao: Colecao = {
  id: 'avaliacao', pai: 'turma', nome: N_('Avaliações e notas'), um: N_('Avaliação'), icone: '📝',
  campos: [
    { k: 'nome', r: N_('Avaliação'), t: 'texto', ph: N_('Ex.: Prova 1, Trabalho em grupo') },
    { k: 'data', r: N_('Data'), t: 'data', aviso: [3, 0] },
    { k: 'max', r: N_('Vale quantos pontos'), t: 'numero' },
    { k: 'peso', r: N_('Peso na média'), t: 'numero' },
    { k: 'conteudo', r: N_('Conteúdo'), t: 'longo' },
  ],
  titulo: r => r.c.nome || '', data: 'data', novo: () => ({ max: 10, peso: 1, notas: {} }), anexos: true,
  agenda: (r, ctx) => ({ title: junta(r.c.nome, ctx.porId(r.pai)?.c.nome) }),
  acoes: (r, ctx) => [{ label: '📤 ' + t('Enviar a nota para cada aluno'), onPress: () => enviarNotas(ctx, r, fmtNota) }],
  lista: Notas,
  chaveAnotacoes: 'anotacoes',   // texto 12: "Anotações" não pode escrever em c.notas (são as notas dos alunos)
};

const aula: Colecao = {
  id: 'aula', check: 'dada', pai: 'turma', nome: N_('Planos de aula'), um: N_('Plano de aula'), icone: r => (r.c.dada ? '✅' : '📚'),
  campos: [
    { k: 'tema', r: N_('Tema'), t: 'texto' },
    { k: 'data', r: N_('Data'), t: 'data' },
    { k: 'hora', r: N_('Horário'), t: 'hora' },
    { k: 'objetivos', r: N_('Objetivos'), t: 'longo' },
    { k: 'atividades', r: N_('Atividades e materiais'), t: 'longo' },
    { k: 'casa', r: N_('Tarefa de casa'), t: 'texto' },
    { k: 'extra', r: N_('Aula extra (fora dos dias de aula)'), t: 'sim' },
    { k: 'dada', r: N_('Aula dada'), t: 'sim' },
  ],
  titulo: r => r.c.tema || '', sub: r => junta(horaTela(r.c.hora), r.c.extra && '➕ ' + t('aula extra'), r.c.casa && t('tarefa: {t}', { t: r.c.casa })), data: 'data', feito: r => !!r.c.dada, anexos: true,
  // a data sugerida é o próximo dia de aula da turma; uma data fora deles marca "Aula extra"
  novo: (_ctx, pai) => ({ data: proximoDiaDeAula(pai) }),
  ajustar: (c, k, _ctx, pai) => (k === 'data' && c.data && !c.extra && diasDaTurma(pai).length && !ehDiaDeAula(diasDaTurma(pai), c.data) ? { ...c, extra: true } : c),
  acoes: (r, ctx) => (r.c.extra && r.c.data ? [{ label: '💰 ' + t('Cobrar a aula extra'), onPress: () => cobrarAulaExtra(ctx, r.pai, r.c.data) }] : []),
  agenda: (r, ctx) => ({ time: r.c.hora || null, title: junta(ctx.porId(r.pai)?.c.nome, r.c.tema), notes: r.c.objetivos || '' }),
};


// ─── Materiais, cobranças e o caderno do professor (0.24) ───
const material: Colecao = {
  id: 'material', pai: 'turma', nome: N_('Materiais'), um: N_('Material'), anexos: true, data: 'liberar', ordem: 'data-desc',
  icone: r => ({ 'Apostila': '📘', 'Lista de exercícios': '📝', 'Slides': '📊', 'Vídeo': '🎬', 'Link': '🔗', 'Mural': '📢' } as Record<string, string>)[r.c.tipo] ?? '📎',
  campos: [
    { k: 'titulo', r: N_('Título'), t: 'texto' },
    { k: 'tipo', r: N_('Tipo'), t: 'opcao', op: [N_('Apostila'), N_('Lista de exercícios'), N_('Slides'), N_('Vídeo'), N_('Link'), N_('Mural')] },
    { k: 'link', r: N_('Link'), t: 'link' },
    { k: 'liberar', r: N_('Liberar para a turma em'), t: 'data', aviso: [0] },
    { k: 'texto', r: N_('Texto ou recado'), t: 'longo' },
    { k: 'enviado', r: N_('Já enviado para a turma'), t: 'sim' },
  ],
  titulo: r => r.c.titulo || op(r.c.tipo), sub: r => junta(op(r.c.tipo), r.c.liberar && t('liberar em {d}', { d: dataBr(r.c.liberar) }), r.c.enviado && '✓ ' + t('enviado')),
  check: 'enviado', feito: r => !!r.c.enviado,
  novo: () => ({ tipo: 'Apostila', liberar: today() }),
  acoes: (r, ctx) => [{ label: '📤 ' + (r.c.tipo === 'Mural' ? t('Enviar o recado para a turma') : t('Enviar para a turma')), onPress: () => enviarMaterial(ctx, r, () => mudarCampos(ctx.mod, r.id, { enviado: true })) }],
  vazio: N_('Apostilas, listas, slides, vídeos, links e recados. Pelo ⋮ › Enviar para a turma, cada aluno recebe no app dele.'),
};

const cobranca: Colecao = {
  id: 'cobranca', pai: 'turma', nome: N_('Cobranças'), um: N_('Cobrança'), semDuplicar: r => !!r.c.pago,   // texto 26 (B7): pagamento já feito
  check: 'pago', data: 'venc',
  pagamento: { tipo: 'receber', venc: 'venc', data: 'pagoEm', valorPago: 'valorPago', forma: 'forma' },
  icone: r => (r.c.pago ? '✅' : r.c.venc && r.c.venc < today() ? '⚠️' : '💰'),
  campos: [
    { k: 'aluno', r: N_('Aluno'), t: 'ref', ref: 'aluno', refPai: true },
    { k: 'tipo', r: N_('Tipo'), t: 'opcao', op: [N_('Mensalidade'), N_('Avulsa'), N_('Aula extra')] },
    { k: 'ref', r: N_('Referente a'), t: 'texto', ph: N_('Ex.: Mensalidade de outubro, material') },
    { k: 'venc', r: N_('Vencimento'), t: 'data', aviso: [2, 0, -1] },
    { k: 'valor', r: N_('Valor'), t: 'dinheiro' },
    { k: 'pago', r: N_('Pago'), t: 'sim', seg: 'fin' },
    { k: 'pagoEm', r: N_('Data do pagamento'), t: 'data', mostrar: c => !!c.pago, seg: 'fin' },
    { k: 'valorPago', r: N_('Valor pago'), t: 'dinheiro', mostrar: c => !!c.pago },
    { k: 'forma', r: N_('Forma de pagamento'), t: 'opcao', op: [N_('PIX'), N_('Dinheiro'), N_('Cartão'), N_('Boleto'), N_('Transferência')], mostrar: c => !!c.pago, seg: 'fin' },
  ],
  titulo: (r, ctx) => junta(ctx.porId(r.c.aluno)?.c.nome, r.c.ref), sub: (r, ctx) => junta(r.c.tipo && r.c.tipo !== 'Mensalidade' && op(r.c.tipo), dinDe(r, 'valor', ctx.moeda), r.c.pago ? t('pago em {d}', { d: dataBr(r.c.pagoEm) }) : dataBr(r.c.venc)),
  feito: r => !!r.c.pago, anexos: true,
  ajustar: (c, k, ctx, pai) => {
    if (k === 'pago') return { ...c, pagoEm: c.pago ? (c.pagoEm || today()) : '' };
    // mensalidade: o valor do aluno (se for diferente) ou o da turma; aula extra: o valor da aula extra da turma
    if ((k === 'tipo' || k === 'aluno') && c.tipo !== 'Avulsa') {
      const a = ctx.porId(c.aluno), v = c.tipo === 'Aula extra' ? pai?.c.valorExtra : temNum(a?.c.valor) ? a!.c.valor : pai?.c.mensalidade;
      return temNum(v) ? { ...c, valor: v } : c;
    }
    return c;
  },
  novo: (_c, pai) => ({ tipo: 'Mensalidade', valor: pai?.c.mensalidade ?? '', venc: today() }),
  painel: CobrarTurma,
  acoes: (r, ctx) => { const a = ctx.porId(r.c.aluno); return a?.c.telefone && !r.c.pago ? [{ label: '💬 ' + t('Lembrete pelo WhatsApp'), onPress: () => whatsapp(a.c.telefone,
    t('Olá! Passando para lembrar do pagamento de {ref} ({v}), com vencimento em {d}. Obrigado(a)!', { ref: r.c.ref || t('mensalidade'), v: dinDe(r, 'valor', ctx.moeda), d: dataBr(r.c.venc) })) }] : []; },
  // 0.69 (B1): cada cobrança pode ter a sua moeda; os totais ficam separados por moeda
  resumo: (l, ctx) => { if (!l.length) return null; const rec = somaPorMoeda(l.filter(r => r.c.pago), r => num(r.c.valor), ctx.moeda), ab = somaPorMoeda(l.filter(r => !r.c.pago), r => num(r.c.valor), ctx.moeda);
    return junta(t('Recebido: {v}', { v: dinSomas(rec, ctx.moeda) }), temSoma(ab) && t('A receber: {v}', { v: dinSomas(ab, ctx.moeda) })); },
  filtros: [[N_('Em aberto'), r => !r.c.pago], [N_('Atrasadas'), r => !r.c.pago && !!r.c.venc && r.c.venc < today()], [N_('Pagas'), r => !!r.c.pago],
    [N_('Avulsas'), r => r.c.tipo === 'Avulsa' || r.c.tipo === 'Aula extra']],
  vazio: N_('Mensalidades, aulas avulsas e materiais. Em ⋮ da turma, "Gerar as cobranças do mês" cria uma para cada aluno.'),
};

/** Acima das cobranças da turma (0.65): as do mês e uma cobrança avulsa para todos os alunos de uma vez. */
function CobrarTurma({ ctx, pai, pode }: PainelProps) {
  const [aberto, setAberto] = useState(false);
  const [ref, setRef] = useState('');
  const [valor, setValor] = useState('');
  const [venc, setVenc] = useState(today());
  const [picker, setPicker] = useState(false);
  if (!pai || pode === false) return null;
  const alunos = alunosDe(ctx, pai.id);
  function gerar() {
    if (!ref.trim()) return Alert.alert(t('Escreva a que se refere a cobrança.'));
    if (!temNum(valor)) return Alert.alert(t('Coloque o valor.'));
    const n = cobrarAlunos(ctx, pai!, alunos.map(a => a.id), { ref: ref.trim(), valor: num(valor), venc, tipo: 'Avulsa' });
    if (!n) return Alert.alert(t('Nada a gerar'), t('Todos os alunos já têm uma cobrança com esta referência.'));
    Alert.alert(t('Pronto'), t('{n} cobrança(s) criada(s) com vencimento em {d}.', { n, d: dataBr(venc) }));
    setAberto(false); setRef(''); setValor('');
  }
  return (
    <View style={{ gap: 8 }}>
      <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap' }}>
        <Chip small cor={ctx.cor} on={false} label={'💰 ' + t('Gerar as cobranças do mês')} onPress={() => gerarCobrancas(ctx, pai)} />
        <Chip small cor={ctx.cor} on={aberto} label={'🧾 ' + t('Cobrar a turma toda')} onPress={() => setAberto(x => !x)} />
      </View>
      {aberto && (
        <View style={{ backgroundColor: C.card, borderRadius: 14, padding: 12, gap: 8 }}>
          <Text style={{ color: C.sub, fontSize: 12 }}>{t('Uma cobrança avulsa para cada um dos {n} aluno(s). Para cobrar só um aluno, toque em +.', { n: alunos.length })}</Text>
          <Field value={ref} onChangeText={setRef} placeholder={t('Referente a (ex.: material, passeio, inscrição)')} />
          <Field value={valor} onChangeText={setValor} keyboardType="decimal-pad" placeholder={t('Valor por aluno')} />
          <Chip small cor={ctx.cor} on label={'📅 ' + t('Vencimento: {d}', { d: dataBr(venc) })} onPress={() => setPicker(true)} />
          <Btn cor={ctx.cor} label={t('Criar as cobranças')} onPress={gerar} disabled={!alunos.length} />
        </View>
      )}
      {picker && <PickerModal mode="date" cor={ctx.cor} value={parseYmd(venc)} onPick={d => setVenc(ymd(d))} onClose={() => setPicker(false)} />}
    </View>
  );
}

/** Uma cobrança do mês para cada aluno da turma (valor do aluno ou a mensalidade da turma). */
function gerarCobrancas(ctx: Ctx, turmaR: Reg) {
  const d = new Date();
  const mes = d.toLocaleDateString(undefined, { month: 'long' });
  const dia = Math.min(28, Math.max(1, num(turmaR.c.diaVenc) || 10));
  const venc = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
  const ref = t('Mensalidade de {m}', { m: mes });
  const ja = new Set(ctx.filhos(turmaR.id, 'cobranca').filter(c => c.c.ref === ref).map(c => c.c.aluno));
  const novas = alunosDe(ctx, turmaR.id).filter(a => !ja.has(a.id)).map(a => ({ a, v: temNum(a.c.valor) ? a.c.valor : turmaR.c.mensalidade }))
    .filter(x => temNum(x.v)).map(x => novoReg('cobranca', turmaR.id, { aluno: x.a.id, ref, venc, valor: x.v, pago: false, tipo: 'Mensalidade' }));
  if (!novas.length) return Alert.alert(t('Nada a gerar'), t('Coloque o valor da mensalidade na turma (Editar) ou no aluno. As cobranças deste mês que já existem não se repetem.'));
  salvarVarios(ctx.mod, novas);
  Alert.alert(t('Pronto'), t('{n} cobrança(s) criada(s) com vencimento em {d}.', { n: novas.length, d: dataBr(venc) }));
}

const caderno: Colecao = {
  id: 'caderno', nome: N_('Meu caderno'), um: N_('Anotação do professor'), anexos: true, ordem: 'data-desc', data: 'data',
  icone: r => ({ 'Estratégia': '🧠', 'Depois da aula': '💬', 'Referência': '📚' } as Record<string, string>)[r.c.tipo] ?? '🗒️',
  campos: [
    { k: 'titulo', r: N_('Título'), t: 'texto' },
    { k: 'tipo', r: N_('Tipo'), t: 'opcao', op: [N_('Estudo'), N_('Estratégia'), N_('Depois da aula'), N_('Referência')] },
    { k: 'turma', r: N_('Turma (opcional)'), t: 'ref', ref: 'turma' },
    { k: 'data', r: N_('Data'), t: 'data' },
    { k: 'texto', r: N_('Anotações'), t: 'longo', ph: N_('O que funcionou, o que ajustar, metodologias, resumos...') },
    { k: 'link', r: N_('Link'), t: 'link' },
  ],
  titulo: r => r.c.titulo || op(r.c.tipo), sub: (r, ctx) => junta(op(r.c.tipo), ctx.porId(r.c.turma)?.c.nome, dataBr(r.c.data)),
  novo: () => ({ tipo: 'Estudo', data: today() }),
  vazio: N_('Só seu: estudos, estratégias pedagógicas e o que funcionou ou não em cada aula. Os alunos não veem.'),
};

/** 0.65 (B15): relatório da turma — alunos com frequência e média, chamadas, notas de cada avaliação e cobranças. */
function relatorioTurma(ctx: Ctx, tr: Reg) {
  const $ = (x: unknown) => din(x, ctx.moeda);
  const alunos = alunosDe(ctx, tr.id);
  const minimo = num(tr.c.media) || 6;
  const chamadas = porDataPdf(ctx.filhos(tr.id, 'chamada'), 'data');
  const avals = porDataPdf(ctx.filhos(tr.id, 'avaliacao'), 'data');
  const cobs = porDataPdf(ctx.filhos(tr.id, 'cobranca'), 'venc');
  const nomeAluno = (id: string) => alunos.find(a => a.id === id)?.c.nome ?? '';
  const b: BlocoPdf[] = [
    { t: 'titulo', texto: junta(tr.c.nome || t('Turma'), tr.c.disciplina) },
    ...dadosPdf([[t('Escola'), junta(tr.c.escola, tr.c.sala)], [t('Dias e horários'), tr.c.horario], [t('Média para aprovação'), fmtNota(minimo)],
      [t('Mensalidade ou valor por aluno'), temNum(tr.c.mensalidade) ? $(tr.c.mensalidade) : '']]),
    ...secaoPdf(t('Alunos'), ['#', t('Nome'), t('Responsável'), t('Telefone'), t('Frequência'), t('Falta'), t('Média')],
      alunos.map(a => { const x = situacaoAluno(ctx, a);
        return [num(a.c.numero) ? String(num(a.c.numero)) : '', a.c.nome || '', a.c.responsavel || '', a.c.telefone || '',
          x.freq != null ? `${x.freq}%` : '', x.aulas ? String(x.faltas) : '', x.media != null ? fmtNota(x.media) + (x.media < minimo ? ' (!)' : '') : '']; }),
      [0.06, 0.28, 0.2, 0.16, 0.11, 0.08, 0.11]),
  ];
  // notas: uma tabela com até 5 avaliações por vez (aluno × avaliação)
  for (let i = 0; i < avals.length; i += 5) {
    const grupo = avals.slice(i, i + 5);
    const w = 0.7 / grupo.length;
    b.push(...secaoPdf(t('Avaliações e notas'), [t('Aluno'), ...grupo.map(v => junta(v.c.nome || t('Avaliação'), dataBr(v.c.data), t('vale {n}', { n: num(v.c.max) || 10 })))],
      alunos.map(a => [a.c.nome || '', ...grupo.map(v => { const n = v.c.notas?.[a.id]; return n === undefined || n === '' || n === null ? '' : fmtNota(num(n)); })]),
      [0.3, ...grupo.map(() => w)]));
  }
  b.push(...secaoPdf(t('Chamada'), [t('Data'), t('Presente'), t('Falta')],
    chamadas.map(c => { const daTurma = (l: string[] | undefined) => (l ?? []).filter(id => alunos.some(a => a.id === id));
      const f = daTurma(c.c.faltas), o = daTurma(c.c.outros);
      return [dataBr(c.c.data), String(alunos.length - f.length - o.length), f.length ? `${f.length}: ${f.map(nomeAluno).join(', ')}` : '0']; }),
    [0.16, 0.14, 0.7]));
  b.push(...secaoPdf(t('Cobranças'), [t('Aluno'), t('Referente a'), t('Vencimento'), t('Valor'), t('Pago')],
    cobs.map(r => [nomeAluno(r.c.aluno) || ctx.porId(r.c.aluno)?.c.nome || '', r.c.ref || '', dataBr(r.c.venc), dinDe(r, 'valor', ctx.moeda),
      r.c.pago ? junta(dataBr(r.c.pagoEm), op(r.c.forma)) || t('Sim') : t('Não')]), [0.26, 0.28, 0.14, 0.14, 0.18]));
  const res = cobranca.resumo?.(cobs, ctx, tr);
  if (res) b.push({ t: 'par', texto: res, tam: 10 });
  return { nome: junta(t('Turma'), tr.c.nome, tr.c.disciplina), blocos: b };
}

// D4: escolas, universidades e cursos que pagam você (salário fixo ou hora-aula)
const ondeTrabalho = colecoesOndeTrabalho({ tipos: [N_('Escola'), N_('Universidade'), N_('Curso'), N_('Outro')], pagamentos: [SALARIO, HORA_AULA],
  ph: N_('Ex.: Colégio Estadual, Universidade Federal'),
  vazio: N_('Escolas, universidades e cursos que pagam você. Com salário fixo, o app gera o a receber de todo mês; por hora-aula, soma as aulas dadas nas turmas ligadas à instituição.') });

export const PROFESSOR: ModDef = { id: 'professor', colecoes: [turma, aluno, chamada, avaliacao, aula, material, cobranca, caderno, ...ondeTrabalho],
  // texto 12: avaliação em que o "Anotações" gravou texto em c.notas — o texto vai para c.anotacoes e c.notas volta a ser o objeto das notas
  migrar: regs => regs.filter(r => r.col === 'avaliacao' && r.c.notas != null && (typeof r.c.notas !== 'object' || Array.isArray(r.c.notas)))
    .map(r => {
      const txt = typeof r.c.notas === 'string' ? r.c.notas : '';
      const c: Record<string, any> = { notas: {}, notas__rico: null };
      if (txt.trim() || r.c.notas__rico) {
        c.anotacoes = [String(r.c.anotacoes ?? '').trim(), txt.trim()].filter(Boolean).join('\n');
        if (r.c.notas__rico && !r.c.anotacoes__rico) c.anotacoes__rico = r.c.notas__rico;
      }
      return { id: r.id, c };
    }),
};

